/**
 * Rounds a number half-away-from-zero, matching Postgres `round()` on
 * `numeric` rather than JavaScript's `Math.round` (which rounds half-toward-
 * positive-infinity). The two agree on positive halves but disagree on
 * negative halves by one minor unit — `Math.round(-0.5) === 0` whereas
 * Postgres `round(-0.5) === -1`.
 *
 * Used by the canonical Line Item totals calculation so persisted JS-driven
 * totals stay bit-identical to the legacy SQL pipeline for negative-value
 * inputs (refund-style Line Items, downward Change Proposals, adjustments).
 */
export const roundHalfAwayFromZero = (n: number): number => {
  const rounded = n >= 0 ? Math.round(n) : -Math.round(-n);
  // Normalise the negative-zero that `-Math.round(-n)` produces when the
  // magnitude rounds to 0. Postgres `numeric` has no negative-zero concept,
  // so persisting `-0` would diverge from the SQL pipeline by one byte.
  return rounded === 0 ? 0 : rounded;
};

/**
 * Whether a number has at most `places` decimal places, tolerant of IEEE-754
 * representation error so legitimate values (e.g. 1.005 at 3 places) are not
 * wrongly rejected.
 */
export const hasAtMostDecimalPlaces = (
  value: number,
  places: number
): boolean => {
  const scaled = value * 10 ** places;
  return Math.abs(scaled - Math.round(scaled)) <= 1e-6;
};
